import React from 'react';
import { Feature, FeatureSetMap } from '~shared/types/pricing/pricingsvc';
import { DetailsFeatureRuleOptions, DetailsFeatureRules } from '~pricing/transformers/details/types';
import { Packages } from '~shared/constants/pricing';

import {
  getBaseResponseCount,
  getCollectorCreateLimitValue,
  getCompareResults,
  getCreateQuestionLimitValue,
  getTagValue,
  getTeamCollaborationValues,
  getTemplatesEnabledValue,
  getStringValue,
} from '../helpers';

const ALL_BUT_BASIC = [
  Packages.TeamAdvantage,
  Packages.TeamPremier,
  Packages.Enterprise,
  Packages.PremierAnnual,
  Packages.AdvantageAnnual,
  Packages.StandardMonthly,
  Packages.StarterMonthly,
  Packages.StarterAnnual,
  Packages.StandardAnnual,
  Packages.Flex,
];

const ADVANTAGE_AND_ABOVE = [
  Packages.TeamAdvantage,
  Packages.TeamPremier,
  Packages.Enterprise,
  Packages.PremierAnnual,
  Packages.AdvantageAnnual,
];

const teamCollaborationRules = [
  'add_users',
  'build_surveys_together',
  'analyze_results_together',
  'custom_templates',
  'team_themes',
  'shared_library',
  'collaboration',
  'team_ownership',
  'consolidated_billing',
  'user_management',
].reduce(
  (acc, key) => ({
    ...acc,
    [key]: (packageName: Packages): boolean | React.ReactElement => getTeamCollaborationValues(packageName),
  }),
  {}
);

const stringValueRules = ['survey_create_limit'].reduce(
  (acc, key) => ({
    ...acc,
    [key]: (packageName: Packages, feature: Feature): string => getStringValue(feature),
  }),
  {}
);

const enterpriseOnly = [
  'admin_dashboard',
  'team_activity',
  'account_control',
  'global_settings',
  'custom_terms_of_use',
  'enterprise_only_integrations',
  'customer_success_manager',
  'custom_question_bank',
  'custom_subdomain',
].reduce(
  (acc, key) => ({
    ...acc,
    [key]: (packageName: Packages): boolean => packageName === Packages.Enterprise,
  }),
  {}
);

const includeInAllPackages = [
  '508_accessibility',
  'pop_up_collector',
  'real_time_results',
  'share_surveys',
  'statistical_significance',
  'progress_bar',
  'plans_benchmarks',
  'benchmarks',
  'dashboard',
  'send_surveys',
  'password_protection',
  'enable_ip_blocking',
  'mobile_apps_ios_android',
  'video_question_type',
  'rating_question_type',
  'quiz_scoring_and_instant_results',
  'require_answer',
  'validate_answer',
  'randomize_answer_choices',
  'set_survey_end_date',
  'view_respondent_ip_address',
  'record_respondent_email_address',
  'set_max_response_count',
  'essential_question_types',
  'all_languages_supported',
  'pagination',
  'labeling_titles_numbering',
  'collector_mobile_sdk_enabled',
].reduce(
  (acc, key) => ({
    ...acc,
    [key]: (): boolean => true,
  }),
  {}
);

function includeInPackagesOnly(packageName: Packages, packages: string[]): boolean {
  return packages.includes(packageName);
}

export default {
  ...teamCollaborationRules,
  ...stringValueRules,
  ...includeInAllPackages,
  ...enterpriseOnly,
  analyze_can_ta_tag: (packageName: Packages): string => getTagValue(packageName),
  analyze_combine_filters: (packageName: Packages): string | boolean => getCompareResults(packageName),
  contributor_seats: (packageName: Packages): boolean => packageName !== Packages.Enterprise,
  response_count: (packageName: Packages, feature: FeatureSetMap, options: DetailsFeatureRuleOptions): string =>
    getBaseResponseCount(packageName, feature, options),
  word_cloud_combine_hide: (packageName: Packages): boolean => packageName !== Packages.TeamAdvantage,
  create_templates_enabled: (packageName: Packages): string => getTemplatesEnabledValue(packageName),
  a_b_testing: (packageName: Packages): boolean => includeInPackagesOnly(packageName, ADVANTAGE_AND_ABOVE),
  advanced_logic: (packageName: Packages): boolean =>
    includeInPackagesOnly(packageName, [Packages.TeamPremier, Packages.Enterprise, Packages.PremierAnnual]),
  text_formatting: (packageName: Packages): boolean => includeInPackagesOnly(packageName, ADVANTAGE_AND_ABOVE),
  carry_forward: (packageName: Packages): boolean => includeInPackagesOnly(packageName, ADVANTAGE_AND_ABOVE),
  extract_data: (packageName: Packages): boolean => includeInPackagesOnly(packageName, ADVANTAGE_AND_ABOVE),
  track_email_responses: (packageName: Packages): boolean => includeInPackagesOnly(packageName, ADVANTAGE_AND_ABOVE),
  priority_email_support: (packageName: Packages): boolean =>
    ![Packages.Basic, Packages.FormsAnnual, Packages.FormsMonthly].includes(packageName),
  rating_question_type: (packageName: Packages): boolean => includeInPackagesOnly(packageName, ALL_BUT_BASIC),
  download_as_ppt: (packageName: Packages): boolean => includeInPackagesOnly(packageName, ALL_BUT_BASIC),
  collector_create_limit: (packageName: Packages, feature: Feature): string =>
    packageName === Packages.Basic ? getCollectorCreateLimitValue(feature) : getStringValue(feature),
  create_question_limit: (packageName: Packages, feature: Feature): string =>
    packageName === Packages.Basic ? getCreateQuestionLimitValue(feature) : getStringValue(feature),
  collector_thank_you_enabled: (packageName: Packages): boolean =>
    includeInPackagesOnly(packageName, ADVANTAGE_AND_ABOVE),
} as DetailsFeatureRules;
